import { useEffect, useMemo, useState } from 'preact/hooks'
import { ShieldCheck, Megaphone, Sparkles } from 'lucide-preact'
import { RadioGroup } from '@/components/common/RadioGroup'
import type { EmailSendRuleDto } from '@/types/touchPoint'
import { EmailComplianceClass } from '@/types/touchPoint'
import { isTransactionalAllowed, resolveComplianceClass } from '@/utils/complianceClassResolver'

interface ConsentClassChipProps {
  /** Current consent class, or null ("Auto") when the server smart-defaults it from the trigger. */
  value: EmailComplianceClass | null
  onChange: (value: EmailComplianceClass | null) => void
  /** The live send rule — drives the resolved "Auto" preview (mirrors the server default). */
  rule: EmailSendRuleDto
}

const MARKETING_OPTION = {
  value: EmailComplianceClass.Marketing,
  label: 'Marketing',
  description: 'Honors opt-outs',
}
const TRANSACTIONAL_OPTION = {
  value: EmailComplianceClass.Transactional,
  label: 'Transactional',
  description: 'Always delivered',
}

// Human label for the consent meaning of a resolved class.
function consentMeaning(consentClass: EmailComplianceClass): string {
  return consentClass === EmailComplianceClass.Transactional ? 'always delivered' : 'honors opt-outs'
}

/**
 * Consent-class control for an email touchpoint (ORB-340/365).
 *
 * Default state (value === null) is "Auto": the chip shows the *resolved* class the server will apply
 * (derived from the rule via the client mirror of GetDefaultComplianceClass) with an "auto" tag, plus
 * an Override affordance. Overriding reveals the explicit Marketing/Transactional pick (RadioGroup) and
 * sets a non-null value; "Reset to Auto" returns to null. null === Auto semantics are preserved exactly
 * so the panel's save path (`ComplianceClass ?? null`) and dirty-check are unchanged.
 *
 * If the rule is malformed and resolveComplianceClass throws (shouldn't happen for valid rules), the
 * Auto chip degrades to a neutral label rather than crashing the panel.
 */
export function ConsentClassChip({ value, onChange, rule }: ConsentClassChipProps) {
  // Local-only: whether the explicit override picker is revealed while value is still null. Once the
  // user actually picks a class, value goes non-null and the picker stays open by virtue of value.
  const [overriding, setOverriding] = useState(false)

  // Resolve the Auto preview defensively — a bad rule degrades to a neutral chip, never throws to render.
  const resolved = useMemo(() => {
    try {
      return resolveComplianceClass(rule)
    } catch {
      return null
    }
  }, [rule])

  // ORB-508: Transactional bypasses the recipient's opt-out, so it is only offered when this rule's
  // audience actually opted in. On a cold blast, a pre-event reminder or the click nudge the server
  // rejects the save outright — James on the compliance gap: "we shouldn't be able to do that… that
  // would bypass your opt-out lists." Hiding the option is how the editor stops offering it.
  const transactionalAllowed = useMemo(() => isTransactionalAllowed(rule), [rule])
  const options = transactionalAllowed
    ? [MARKETING_OPTION, TRANSACTIONAL_OPTION]
    : [MARKETING_OPTION]

  // Changing the anchor can invalidate a Transactional override that was legal when it was picked
  // (e.g. re-pointing a delivery email at the click). Drop it back to Auto rather than leaving a
  // selection the picker no longer shows and the server will refuse.
  //
  // Closing the override picker at the same time is not cosmetic: showPicker is
  // `value !== null || overriding`, so clearing the value alone strands the user in a picker with
  // NOTHING selected, a lone Marketing option, and a "Reset to Auto" button for a state that is
  // already Auto — while hiding the Auto chip that would have explained what just changed.
  // Collapsing back to the chip is also the honest end state: Auto resolves to Marketing here.
  useEffect(() => {
    if (!transactionalAllowed && value === EmailComplianceClass.Transactional) {
      onChange(null)
      setOverriding(false)
    }
  }, [transactionalAllowed, value, onChange])

  const showPicker = value !== null || overriding
  // Neutral Sparkles when the rule couldn't resolve (resolved === null), so the icon never implies
  // Marketing while the label has fallen back to "Auto".
  const ResolvedIcon =
    resolved === EmailComplianceClass.Transactional ? ShieldCheck
    : resolved === EmailComplianceClass.Marketing ? Megaphone
    : Sparkles

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
        Consent class
      </label>

      {value === null && !overriding && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-200">
            <ResolvedIcon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
            {resolved
              ? <>{resolved} <span className="text-slate-400 dark:text-slate-500">— {consentMeaning(resolved)}</span></>
              : 'Auto'}
            <span className="ml-0.5 rounded bg-slate-200/80 dark:bg-slate-700 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              auto
            </span>
          </span>
          <button
            type="button"
            onClick={() => setOverriding(true)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            Override
          </button>
        </div>
      )}

      {showPicker && (
        <div className="space-y-1.5">
          <RadioGroup
            name="ComplianceClass"
            layout="grid"
            options={options}
            value={value ?? undefined}
            onChange={next => onChange(next as EmailComplianceClass)}
          />
          {!transactionalAllowed && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              This email reaches people who haven't signed up, so it can only be sent as Marketing.
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              onChange(null)
              setOverriding(false)
            }}
            className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            Reset to Auto
          </button>
        </div>
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Auto picks the class from the trigger. Marketing honors opt-outs; transactional always sends.
      </p>
    </div>
  )
}
